import { useEffect, useState, type FormEvent } from "react";

import { apiClient, type ResourceGrant, type ResourceType } from "../../api/client";
import { errorMessage } from "../admin/errors";
import { ConfirmationDialog } from "./ConfirmationDialog";
import type { GrantDraft, PendingGrantAction, UserSectionProps } from "./UserDetailDrawer";

export function UserGrantsSection({ user }: Pick<UserSectionProps, "user">) {
  const [grants, setGrants] = useState<ResourceGrant[]>([]);
  const [draft, setDraft] = useState<GrantDraft>({ resourceType: "store", resourceId: "" });
  const [pending, setPending] = useState<PendingGrantAction | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    apiClient
      .listUserGrants(user.id)
      .then(setGrants)
      .catch((caught: unknown) => setError(errorMessage(caught)))
      .finally(() => setLoading(false));
  }, [user.id]);

  function prepareAdd(event: FormEvent) {
    event.preventDefault();
    if (draft.resourceId.trim()) setPending({ kind: "add" });
  }

  async function confirmAction() {
    if (!pending) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (pending.kind === "add") {
        const created = await apiClient.addUserGrant(
          user.id,
          draft.resourceType,
          draft.resourceId.trim(),
        );
        setGrants((current) => [...current, created]);
        setDraft((current) => ({ ...current, resourceId: "" }));
        setMessage("资源授权已添加。");
      } else if (pending.grant) {
        await apiClient.revokeUserGrant(user.id, pending.grant.id);
        setGrants((current) => current.filter((grant) => grant.id !== pending.grant?.id));
        setMessage("资源授权已撤销。");
      }
      setPending(null);
    } catch (caught) {
      setPending(null);
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  const confirmation = pending ? grantConfirmation(pending, draft) : null;

  return (
    <section className="user-detail-section" aria-labelledby="grants-section-title">
      <div>
        <span className="eyebrow">授权</span>
        <h3 id="grants-section-title">资源授权</h3>
      </div>
      <form className="user-grant-form" onSubmit={prepareAdd}>
        <label>
          资源类型
          <select
            onChange={(event) =>
              setDraft({ ...draft, resourceType: event.target.value as ResourceType })
            }
            value={draft.resourceType}
          >
            {(["department", "store", "brand", "site", "dataset"] as ResourceType[]).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label>
          资源 UUID
          <input
            onChange={(event) => setDraft({ ...draft, resourceId: event.target.value })}
            placeholder="00000000-0000-0000-0000-000000000000"
            required
            value={draft.resourceId}
          />
        </label>
        <button disabled={busy || !draft.resourceId.trim()} type="submit">
          添加授权
        </button>
      </form>
      <div className="grant-list">
        {loading && <p className="panel-copy">正在读取授权…</p>}
        {!loading && !grants.length && <p className="panel-copy">暂无当前可管理授权。</p>}
        {grants.map((grant) => (
          <div key={grant.id}>
            <span>
              {grant.resource_type} · {grant.resource_id}
            </span>
            <button
              className="text-button"
              disabled={busy}
              onClick={() => setPending({ kind: "revoke", grant })}
              type="button"
            >
              撤销
            </button>
          </div>
        ))}
      </div>
      {message && <p className="status-banner">{message}</p>}
      {error && (
        <p className="alert error" role="alert">
          {error}
        </p>
      )}
      {confirmation && pending && (
        <ConfirmationDialog
          busy={busy}
          confirmLabel={confirmation.confirmLabel}
          description={confirmation.description}
          onCancel={() => setPending(null)}
          onConfirm={() => void confirmAction()}
          title={confirmation.title}
        />
      )}
    </section>
  );
}

function grantConfirmation(pending: PendingGrantAction, draft: GrantDraft) {
  if (pending.kind === "add") {
    return {
      title: "确认添加授权",
      description: `向目标账户添加 ${draft.resourceType} 资源 ${draft.resourceId.trim()} 的访问权限。`,
      confirmLabel: "确认添加",
    };
  }
  return {
    title: "确认撤销授权",
    description: `撤销目标账户对 ${pending.grant?.resource_type} 资源 ${pending.grant?.resource_id} 的访问权限。`,
    confirmLabel: "确认撤销",
  };
}
